import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Bell, ChevronDown, User, LogOut, Settings } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { notificationsAPI } from '../../services/api';
import MessageModal from '../../components/common/MessageModal';
import GlobalSearch from '../../components/common/GlobalSearch';
import './Navbar.css';

const formatNotificationTime = (createdAt) => {
  if (!createdAt) return 'Just now';
  const d = new Date(createdAt);
  if (Number.isNaN(d.getTime())) return 'Just now';
  const diffMinutes = Math.max(1, Math.floor((Date.now() - d.getTime()) / 60000));
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString();
};

const routeTitles = {
  '/': 'Dashboard',
  '/content-library': 'Content Library',
  '/virtual-tours': 'Virtual Tours',
  '/explainer-videos': 'Explainer Videos',
  '/modules': 'Modules',
  '/enrollment': 'User Enrollment',
  '/analytics': 'Analytics & Reports',
  '/analytics/leaderboard': 'Trainee Leaderboard',
  '/notifications': 'Notifications',
  '/vr-access': 'VR Access',
  '/vr-access/channels': 'VR Channels',
  '/settings': 'Settings',
  '/profile': 'My Profile',
  '/help': 'Help & Guide',
};

const getPageTitle = (pathname) => {
  if (routeTitles[pathname]) return routeTitles[pathname];
  if (pathname.startsWith('/content-library/')) return 'Content Details';
  if (pathname.startsWith('/modules/')) return 'Module Details';
  if (pathname.startsWith('/explainer-videos/')) return 'Video Details';
  if (pathname.startsWith('/virtual-tours/')) return 'Tour Details';
  if (pathname.startsWith('/analytics/module/')) return 'Module Analytics';
  if (pathname.startsWith('/analytics/trainee/')) return 'Trainee Analytics';
  if (pathname.startsWith('/trainees/')) return 'Trainee Details';
  return 'EndoNurseVR LMS';
};

const Navbar = () => {
  const { sidebarCollapsed, currentUser, logout, showNotification } = useAppContext();
  const [profileOpen, setProfileOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [messageRecipient, setMessageRecipient] = useState('');
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const profileRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const pageTitle = getPageTitle(location.pathname);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
        setNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load in-app notifications for the current user
  useEffect(() => {
    let isActive = true;
    const loadNotifications = async () => {
      if (!currentUser?.id) {
        if (isActive) { setNotifications([]); setUnreadCount(0); }
        return;
      }
      try {
        const response = await notificationsAPI.getNotifications({ limit: 20 });
        const data = response.data?.data;
        if (!isActive) return;
        setNotifications(data?.items || []);
        setUnreadCount(data?.unreadCount || 0);
      } catch (error) {
        // Silent on transient/server errors; the bell just shows no new items.
      }
    };
    loadNotifications();
    return () => { isActive = false; };
  }, [currentUser?.id, notifOpen]);

  const handleNotificationClick = async (n) => {
    setNotifOpen(false);
    if (!n.isRead) {
      try {
        await notificationsAPI.markAsRead(n.id);
        setUnreadCount((c) => Math.max(0, c - 1));
        setNotifications((prev) => prev.map((it) => (it.id === n.id ? { ...it, isRead: true } : it)));
      } catch (error) { /* non-blocking */ }
    }
    navigate(n.actionUrl || '/notifications');
  };

  const handleEmailClick = (email) => {
    setMessageRecipient(email);
    setMessageModalOpen(true);
  };

  const handleSendMessage = ({ recipientEmail, subject, message }) => {
    // In a real app, you would send the message via API
    showNotification(`Message sent to ${recipientEmail} successfully!`, 'success');
    setMessageModalOpen(false);
  };

  return (
    <header
      className="navbar"
      style={{
        left: sidebarCollapsed
          ? 'var(--sidebar-collapsed-width)'
          : 'var(--sidebar-width)',
      }}
    >
      {/* Left: Page Title */}
      <div className="navbar__left">
        <div className="navbar__page-title">
          <h2 className="navbar__title">{pageTitle}</h2>
          <span className="navbar__breadcrumb">ChenaiXR Training LMS</span>
        </div>
      </div>

      {/* Center: Global Search */}
      <GlobalSearch />

      {/* Right: Actions */}
      <div className="navbar__right" ref={profileRef}>
        {/* Notifications */}
        <div className="navbar__notif-wrapper">
          <button
            className="navbar__icon-btn"
            onClick={() => {
              setNotifOpen((p) => !p);
              setProfileOpen(false);
            }}
          >
            <Bell size={19} strokeWidth={1.8} />
            {unreadCount > 0 && (
              <span className="navbar__badge">{unreadCount}</span>
            )}
          </button>

          {notifOpen && (
            <div className="navbar__dropdown navbar__notif-dropdown">
              <div className="navbar__dropdown-header">
                <span>Notifications</span>
                <span className="navbar__badge-pill">{unreadCount} new</span>
              </div>
              <div className="navbar__notif-list">
                {notifications.length === 0 ? (
                  <div className="navbar__notif-item">
                    <div className="navbar__notif-content">
                      <p className="navbar__notif-text">You're all caught up.</p>
                    </div>
                  </div>
                ) : (
                  notifications.map((n) => (
                    <button
                      key={n.id}
                      type="button"
                      className={`navbar__notif-item ${!n.isRead ? 'navbar__notif-item--unread' : ''}`}
                      onClick={() => handleNotificationClick(n)}
                      style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer' }}
                    >
                      <div className="navbar__notif-dot" />
                      <div className="navbar__notif-content">
                        <p className="navbar__notif-text"><strong>{n.title}</strong> — {n.message}</p>
                        <span className="navbar__notif-time">{formatNotificationTime(n.createdAt)}</span>
                      </div>
                    </button>
                  ))
                )}
              </div>
              <button
                type="button"
                className="navbar__dropdown-item"
                onClick={() => { setNotifOpen(false); navigate('/notifications'); }}
                style={{ width: '100%', justifyContent: 'center', cursor: 'pointer' }}
              >
                View all notifications
              </button>
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="navbar__divider" />

        {/* Profile */}
        <div className="navbar__profile-wrapper">
          <button
            className="navbar__profile-btn"
            onClick={() => {
              setProfileOpen((p) => !p);
              setNotifOpen(false);
            }}
          >
            <div className="navbar__avatar">
              {currentUser.avatar ? (
                <img src={currentUser.avatar} alt={currentUser.name} />
              ) : (
                <span>{currentUser.initials}</span>
              )}
            </div>
            <div className="navbar__profile-info">
              <span className="navbar__profile-name">{currentUser.name}</span>
              <span className="navbar__profile-role">{currentUser.role}</span>
            </div>
            <ChevronDown
              size={14}
              className={`navbar__chevron ${profileOpen ? 'navbar__chevron--open' : ''}`}
            />
          </button>

          {profileOpen && (
            <div className="navbar__dropdown navbar__profile-dropdown">
              <div className="navbar__dropdown-user">
                <div className="navbar__avatar navbar__avatar--lg">
                  {currentUser.avatar ? (
                    <img src={currentUser.avatar} alt={currentUser.name} />
                  ) : (
                    <span>{currentUser.initials}</span>
                  )}
                </div>
                <div>
                  <p className="navbar__dropdown-name">{currentUser.name}</p>
                  <button
                    className="navbar__dropdown-email link"
                    onClick={() => handleEmailClick(currentUser.email)}
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' }}
                  >
                    {currentUser.email}
                  </button>
                </div>
              </div>
              <div className="navbar__dropdown-divider" />
              <button
                className="navbar__dropdown-item"
                onClick={() => {
                  navigate('/profile');
                  setProfileOpen(false);
                }}
              >
                <User size={15} />
                <span>My Profile</span>
              </button>
              {currentUser.role !== 'Trainee' && (
                <button
                  className="navbar__dropdown-item"
                  onClick={() => {
                    navigate('/settings');
                    setProfileOpen(false);
                  }}
                >
                  <Settings size={15} />
                  <span>Settings</span>
                </button>
              )}
              <div className="navbar__dropdown-divider" />
              <button
                className="navbar__dropdown-item navbar__dropdown-item--danger"
                onClick={() => {
                  logout();
                  setProfileOpen(false);
                  navigate('/login');
                }}
              >
                <LogOut size={15} />
                <span>Sign Out</span>
              </button>
            </div>
          )}
        </div>
      </div>
      <MessageModal
        isOpen={messageModalOpen}
        onClose={() => setMessageModalOpen(false)}
        recipientEmail={messageRecipient}
        currentUserRole={currentUser?.role}
        onSend={handleSendMessage}
      />
    </header>
  );
};

export default Navbar;
